import {
  getDisplayValue,
  getItemsForLesson,
  getLessonById,
  getLessons,
  getWordItems,
  type PronunciationItem,
  type PronunciationScript
} from '@/content/pronunciation'
import { getBaseFromPinyinNumber, tokenizePinyinNumber } from './pinyin'

export type PronunciationQuizMode = 'lesson' | 'confusion' | 'weak'

export type PronunciationQuizOption = {
  id: string
  label: string
  itemId: string
  audioPath?: string
  hanzi?: string
  meaning?: string
  pinyin: string
  zhuyin: string
}

export type PronunciationQuizQuestionType =
  | 'audio-to-script'
  | 'word-audio-to-script'
  | 'script-to-audio'

export type PronunciationQuizQuestion = {
  id: string
  type: PronunciationQuizQuestionType
  prompt: string
  item: PronunciationItem
  script: PronunciationScript
  options: PronunciationQuizOption[]
  correctOptionId: string
  soundTags: string[]
  confusionTags: string[]
}

export type BuildQuizQuestionsOptions = {
  lessonId?: string
  script: PronunciationScript
  mode?: PronunciationQuizMode
  confusionTag?: string
  weakSoundTags?: string[]
  count?: number
  seed?: string
}

export function buildQuizQuestions({
  lessonId,
  script,
  mode = 'lesson',
  confusionTag,
  weakSoundTags = [],
  count = 10,
  seed = `${script}:${lessonId ?? 'all'}:${mode}:${confusionTag ?? ''}`
}: BuildQuizQuestionsOptions): PronunciationQuizQuestion[] {
  const rng = createSeededRandom(seed)
  const lesson = lessonId ? getLessonById(lessonId) : null
  const currentItems = lesson
    ? getItemsForLesson(lesson.id).words
    : getWordItems()
  const previousItems = lesson
    ? getLessons()
        .filter((candidate) => candidate.order < lesson.order)
        .flatMap((candidate) => getItemsForLesson(candidate.id).words)
    : []

  const weakItems = weakSoundTags.length
    ? getWordItems().filter((item) =>
        item.soundTags.some((tag) => weakSoundTags.includes(tag))
      )
    : []

  const confusionItems = confusionTag
    ? getWordItems().filter((item) =>
        item.confusionTags?.includes(confusionTag)
      )
    : []

  const pool =
    mode === 'confusion'
      ? confusionItems
      : mode === 'weak'
        ? weakItems
        : weightedPool(currentItems, previousItems, weakItems)

  const usablePool = uniqueItems(pool).filter((item) => item.audioPath)
  const fallbackPool = uniqueItems([
    ...usablePool,
    ...currentItems,
    ...previousItems,
    ...getWordItems()
  ]).filter((item) => item.audioPath)

  const selected = shuffle(
    usablePool.length >= 4 ? usablePool : fallbackPool,
    rng
  ).slice(0, Math.max(1, count))

  return selected.flatMap((item, index) => {
    const type: PronunciationQuizQuestionType =
      index % 2 === 0 ? 'word-audio-to-script' : 'script-to-audio'
    const question = createQuestion({
      item,
      type,
      script,
      allItems: fallbackPool,
      rng,
      index
    })
    return question ? [question] : []
  })
}

export function scoreQuizAnswer(
  question: PronunciationQuizQuestion,
  optionId: string
) {
  return question.correctOptionId === optionId
}

function createQuestion({
  item,
  type,
  script,
  allItems,
  rng,
  index
}: {
  item: PronunciationItem
  type: PronunciationQuizQuestionType
  script: PronunciationScript
  allItems: PronunciationItem[]
  rng: () => number
  index: number
}): PronunciationQuizQuestion | null {
  const optionItems = getOptionItems(item, allItems, script, rng)
  if (optionItems.length < 2) return null

  const options = optionItems.map((optionItem, optionIndex) => ({
    id: `${item.id}-${index}-${optionIndex}`,
    label: getDisplayValue(optionItem, script),
    itemId: optionItem.id,
    audioPath: optionItem.audioPath,
    hanzi: optionItem.hanzi,
    meaning: optionItem.meaning,
    pinyin: optionItem.pinyin,
    zhuyin: optionItem.zhuyin
  }))

  const correctIndex = optionItems.findIndex(
    (optionItem) => optionItem.id === item.id
  )

  const prompt =
    type === 'word-audio-to-script'
      ? `Choose the ${scriptLabel(script)} spelling for the word you hear.`
      : `Listen and choose the correct sound for the spelling shown.`

  return {
    id: `${type}-${script}-${item.id}-${index}`,
    type,
    prompt,
    item,
    script,
    options,
    correctOptionId: options[Math.max(0, correctIndex)]?.id ?? options[0].id,
    soundTags: item.soundTags,
    confusionTags: item.confusionTags ?? []
  }
}

function getBasePattern(pinyinNumber?: string): string {
  if (!pinyinNumber) return ''
  return tokenizePinyinNumber(pinyinNumber)
    .map((syllable) => getBaseFromPinyinNumber(syllable))
    .join(' ')
}

function getOptionItems(
  item: PronunciationItem,
  allItems: PronunciationItem[],
  script: PronunciationScript,
  rng: () => number
) {
  const sameTypeItems = allItems.filter((candidate) => candidate.id !== item.id)

  const basePattern = item.pinyinNumber ? getBasePattern(item.pinyinNumber) : ''

  const sameBaseItems = basePattern
    ? sameTypeItems.filter(
        (candidate) =>
          candidate.pinyinNumber &&
          getBasePattern(candidate.pinyinNumber) === basePattern
      )
    : []

  const confusionItems = item.confusionTags?.length
    ? sameTypeItems.filter((candidate) =>
        candidate.confusionTags?.some((tag) =>
          item.confusionTags?.includes(tag)
        )
      )
    : []

  const closeItems = sameTypeItems.filter((candidate) =>
    candidate.soundTags.some((tag) => item.soundTags.includes(tag))
  )

  const tier0 = shuffle(sameBaseItems, rng)
  const tier1 = shuffle(
    confusionItems.filter((c) => !sameBaseItems.some((x) => x.id === c.id)),
    rng
  )
  const tier2 = shuffle(
    closeItems.filter(
      (c) =>
        !sameBaseItems.some((x) => x.id === c.id) &&
        !confusionItems.some((x) => x.id === c.id)
    ),
    rng
  )
  const tier3 = shuffle(
    sameTypeItems.filter(
      (c) =>
        !sameBaseItems.some((x) => x.id === c.id) &&
        !confusionItems.some((x) => x.id === c.id) &&
        !closeItems.some((x) => x.id === c.id)
    ),
    rng
  )

  const prioritizedCandidates = [...tier0, ...tier1, ...tier2, ...tier3]

  const uniqueDistractors: PronunciationItem[] = []
  const seenLabels = new Set<string>([getDisplayValue(item, script)])

  for (const candidate of prioritizedCandidates) {
    const label = getDisplayValue(candidate, script)
    if (!seenLabels.has(label)) {
      seenLabels.add(label)
      uniqueDistractors.push(candidate)
      if (uniqueDistractors.length >= 3) break
    }
  }

  return shuffle([item, ...uniqueDistractors], rng)
}

function weightedPool(
  currentItems: PronunciationItem[],
  previousItems: PronunciationItem[],
  weakItems: PronunciationItem[]
) {
  return [
    ...currentItems,
    ...currentItems,
    ...currentItems,
    ...previousItems,
    ...weakItems
  ]
}

function uniqueItems(items: PronunciationItem[]) {
  const seen = new Set<string>()
  return items.filter((item) => {
    if (seen.has(item.id)) return false
    seen.add(item.id)
    return true
  })
}

function shuffle<T>(items: T[], rng: () => number) {
  const next = [...items]
  for (let index = next.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(rng() * (index + 1))
    const current = next[index]
    next[index] = next[swapIndex]
    next[swapIndex] = current
  }
  return next
}

function createSeededRandom(seed: string) {
  let hash = 2166136261
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }

  return () => {
    hash += 0x6d2b79f5
    let value = hash
    value = Math.imul(value ^ (value >>> 15), value | 1)
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61)
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296
  }
}

function scriptLabel(script: PronunciationScript) {
  return script === 'pinyin' ? 'Pinyin' : 'Zhuyin'
}
